import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACCESS_MODULE_DEFS,
  ASSIGNABLE_ROLES,
  MEMBERSHIP_ROLE_MAP,
  PERMISSION_GROUPS,
  PERMISSION_ACTION_MAP,
  withRequired,
  dependentsOf,
  deriveModuleLevels,
  expandLevelsToActions,
  type AccessPermissions,
  type AccessProfileView,
  type MembershipRole,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Pager } from '../../components/ui';
import { Avatar } from '../../components/People';
import { useToast } from '../../components/Toast';

/**
 * أجزاءٌ يُنفَّذ تحكّمُها التفصيليّ على الخادمِ فعلًا.
 *
 * تُعرض في الواجهة الأجزاءُ المُنفَّذةُ وحدَها — لا صناديقُ اختيارٍ لا أثرَ
 * لها. «العملاء المحتملون» أوّلُ نموذجٍ كامل، وتُضاف البقيّةُ على منواله.
 */
const ENFORCED_GROUPS = [
  'crm.leads',
  'crm.accounts',
  'crm.contacts',
  'crm.activities',
  'sales.deals',
  'sales.quotations',
  'sales.contracts',
  'sales.catalog',
  'finance.invoices',
  'finance.payments',
  'finance.expenses',
  'finance.payables',
  'finance.accounting',
  'finance.assets',
  'hr.employees',
  'hr.attendance',
  'hr.leaves',
  'hr.payroll',
  'agency.projects',
  'agency.tasks',
  'agency.templates',
  'callcenter.calls',
  'callcenter.dispositions',
  'reports.builder',
  'reports.joy',
] as const;

/**
 * ملفّاتُ الصلاحيات — كما في زوهو: ملفٌّ يُسمّى ويُعيَّن للأعضاء.
 *
 * الدورُ رتبةٌ (ما يُسمح به من عمليات)، والملفُّ تضييقٌ فوقه وحدةً وحدة:
 * لا شيء، عرض، تعديل، إدارة. فالمحاسبُ «مدير» في الحسابات ولا يرى الموارد
 * البشرية، ومندوبُ المبيعات يرى الفواتير ولا يكتبها. والواجهةُ تُخفي ما
 * مستواه «لا شيء»، والخادمُ يرفض ما يتجاوز المستوى في كلّ طلب.
 */
export function Roles() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<AccessProfileView | 'new' | null>(null);
  const [removing, setRemoving] = useState<AccessProfileView | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /*
   * ارتفاعُ الجدولِ يُقاسُ لا يُخمَّن.
   *
   * التمريرُ داخلَ الجدولِ لا في الصفحة، فيأخذُ ما بقي من ارتفاعِ منطقةِ
   * المحتوى بعد الترويسةِ الثابتة، ويُعادُ القياسُ عند تغيُّرِ حجمِها أو النافذة.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const useTableHeight = (rows: number) =>
    useEffect(() => {
      const page = pageRef.current;
      const sticky = stickyRef.current;
      if (!page || !sticky) return;
      const main = page.closest('.main');
      const update = () => {
        /* الترقيمُ يقفُ أسفلَ الجدولِ داخلَ البطاقة، فيُقتطَعُ ارتفاعُه أيضًا. */
        const pager = page.querySelector<HTMLElement>('.pager');
        const avail =
          (main?.clientHeight ?? window.innerHeight) -
          sticky.offsetHeight -
          (pager?.offsetHeight ?? 0) -
          24;
        page.style.setProperty('--tbl-max', `${Math.max(220, Math.round(avail))}px`);
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(sticky);
      if (main) ro.observe(main);
      return () => ro.disconnect();
      /* يُعادُ القياسُ حين يظهرُ الترقيمُ أو يتغيّرُ عددُ الصفوف. */
    }, [rows]);

  const profiles = useQuery({
    queryKey: ['team', 'profiles'],
    queryFn: () => api<AccessProfileView[]>('/team/profiles'),
  });

  /* الأعضاءُ لإظهار من عُيِّن له كلُّ ملفّ — تقاطعٌ بالمعرّف. */
  const members = useQuery({
    queryKey: ['team', 'members'],
    queryFn: () =>
      api<Array<{ userId: string; name: string; profileId: string | null; jobTitle: string | null }>>(
        '/team/members',
      ),
  });
  const membersByProfile = new Map<string, { userId: string; name: string; jobTitle: string | null }[]>();
  for (const m of members.data ?? []) {
    if (!m.profileId) continue;
    const arr = membersByProfile.get(m.profileId) ?? [];
    arr.push(m);
    membersByProfile.set(m.profileId, arr);
  }

  /* ترقيمٌ محلّيٌّ: الملفّاتُ كاملةٌ في اليد، فالتقطيعُ هنا بلا رحلةٍ للخادم. */
  const allProfiles = profiles.data ?? [];
  const pageCount = Math.max(1, Math.ceil(allProfiles.length / pageSize));
  const pagedProfiles = allProfiles.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  useTableHeight(pagedProfiles.length);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['team'] });

  const seed = useMutation({
    mutationFn: () => api<{ created: number }>('/team/profiles/seed', { method: 'POST' }),
    onSuccess: (r) => {
      refresh();
      toast(
        r.created ? `أُنشئت ${r.created} ملفّات جاهزة` : 'الملفّات الجاهزة موجودة بالفعل',
        'success',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإنشاء'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/team/profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      setRemoving(null);
      toast('حُذف الملفّ', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف'),
  });

  return (
    <div className="page" ref={pageRef}>
      {/* الترويسةُ والشرحُ ثابتان — يبدأُ التمريرُ من قائمةِ الملفّاتِ تحتهما. */}
      <div className="page-sticky" ref={stickyRef}>
      <PageHead
        title="ملفّات الصلاحيات"
        subtitle="ما يُتاح من كلّ وحدةٍ لمن يحمل الملفّ — يُعيَّن للأعضاء من شاشة المستخدمين"
        actions={
          <>
            <Link to="/settings/users" className="btn">
              <Icon name="users" size={15} />
              المستخدمون
            </Link>
            <button className="btn" onClick={() => seed.mutate()} disabled={seed.isPending}>
              ملفّات جاهزة
            </button>
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={15} />
              ملفّ جديد
            </button>
          </>
        }
      />

      <p className="small mute mb-4" style={{ maxWidth: '80ch' }}>
        الدورُ يقول ما تسمح به الرتبة، والملفُّ يضيّق فوقه وحدةً وحدة ولا يوسّع. عضوٌ بلا ملفٍّ يعمل
        بدوره وحده كما كان. تعديلُ ملفٍّ يسري على حامليه خلال دقيقة.
      </p>
      </div>

      {profiles.isError && (
        <ErrorState error={profiles.error} onRetry={() => void profiles.refetch()} />
      )}
      {profiles.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {profiles.data && profiles.data.length === 0 && (
        <EmptyState
          title="لا ملفّات بعد"
          text="أنشئ ملفًّا يحدّد ما يراه فريقك من كلّ وحدة، أو ابدأ من الملفّات الجاهزة."
          action={
            <button className="btn btn-primary" onClick={() => seed.mutate()}>
              أنشئ الملفّات الجاهزة
            </button>
          }
        />
      )}

      {profiles.data && profiles.data.length > 0 && (
        <div className="card">
          <div className="table-wrap tbl-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>الملفّ</th>
                  <th>الوصف</th>
                  <th>الدور الأساسي</th>
                  <th>الموظفون المعيَّنون</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagedProfiles.map((p) => {
                  const assigned = membersByProfile.get(p.id) ?? [];
                  return (
                  <tr key={p.id}>
                    <td>
                      <span className="cell-strong">{p.name}</span>
                    </td>
                    <td className="tiny mute" style={{ maxWidth: '32ch' }}>
                      {p.description || '—'}
                    </td>
                    <td>
                      <Badge kind={MEMBERSHIP_ROLE_MAP[p.baseRole].kind}>
                        {MEMBERSHIP_ROLE_MAP[p.baseRole].label}
                      </Badge>
                    </td>
                    <td>
                      {assigned.length === 0 ? (
                        <span className="tiny mute">لا أحد بعد</span>
                      ) : (
                        <span className="prof-members">
                          {assigned.map((m) => (
                            <span key={m.userId} className="prof-member" title={m.jobTitle ?? undefined}>
                              <Avatar name={m.name} size={20} />
                              {m.name}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-sm" onClick={() => setEditing(p)}>
                          تعديل
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => setRemoving(p)}
                          disabled={p.members > 0}
                          title={p.members > 0 ? 'انقل الأعضاء إلى ملفٍّ آخر أولًا' : undefined}
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pager
            page={page}
            pageCount={pageCount}
            total={allProfiles.length}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {editing && (
        <ProfileDialog
          profile={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {removing && (
        <Modal
          title={`حذف «${removing.name}»`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn" onClick={() => setRemoving(null)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                onClick={() => remove.mutate(removing.id)}
                disabled={remove.isPending}
              >
                حذف
              </button>
            </>
          }
        >
          <p>لا يحمله أحدٌ الآن، وحذفُه لا يمسّ الأعضاء. متأكد؟</p>
        </Modal>
      )}
    </div>
  );
}


/* ==========================================================================
   نافذةُ الملفّ — اسمٌ ودورٌ ومصفوفةُ وحدات
   ========================================================================== */

function ProfileDialog({
  profile,
  onClose,
  onDone,
}: {
  profile: AccessProfileView | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [baseRole, setBaseRole] = useState<MembershipRole>(profile?.baseRole ?? 'EMPLOYEE');
  /*
   * الصلاحياتُ صارت أفعالًا تفصيليّةً فقط (نمط زوهو): مفاتيحُ الأفعالِ
   * المسموحة. تحديدُ فعلٍ يجرّ ما يلزمه، وإزالتُه تُزيل ما يعتمد عليه.
   * ومستوياتُ الوحداتِ تُشتقّ منها آليًّا عند الحفظِ (للمساراتِ غيرِ
   * المُخرَّطة). وملفٌّ قديمٌ يُفرَد مستواه إلى أفعالٍ عند فتحه للتحرير.
   */
  const [actions, setActions] = useState<Set<string>>(() => {
    if (!profile) return new Set();
    if (Array.isArray(profile.permissions.__actions)) {
      return new Set(profile.permissions.__actions);
    }
    return new Set(expandLevelsToActions(profile.permissions));
  });

  const toggleAction = (key: string, on: boolean) =>
    setActions((prev) => {
      const next = new Set(prev);
      if (on) {
        for (const k of withRequired([key])) next.add(k);
      } else {
        next.delete(key);
        const strip = (k: string) => {
          for (const d of dependentsOf(k))
            if (next.has(d)) {
              next.delete(d);
              strip(d);
            }
        };
        strip(key);
      }
      return next;
    });

  /* كلُّ أفعالِ جزءٍ دفعةً — تسهيلًا (تحديدُ الكلِّ يجرّ اللوازم). */
  const setGroup = (groupKey: string, on: boolean) => {
    const g = PERMISSION_GROUPS.find((x) => x.key === groupKey);
    if (!g) return;
    if (on) g.actions.forEach((a) => toggleAction(a.key, true));
    else [...g.actions].reverse().forEach((a) => toggleAction(a.key, false));
  };

  const buildPermissions = (): AccessPermissions => ({
    ...deriveModuleLevels(actions),
    __actions: [...actions],
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || null,
        baseRole,
        permissions: buildPermissions(),
      };
      return profile
        ? api(`/team/profiles/${profile.id}`, { method: 'PATCH', body })
        : api('/team/profiles', { method: 'POST', body });
    },
    onSuccess: onDone,
  });

  return (
    <Modal
      title={profile ? `تعديل «${profile.name}»` : 'ملفّ صلاحيات جديد'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={name.trim().length < 2 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="r-name">
            اسم الملفّ
          </label>
          <input
            id="r-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="مندوب مبيعات، محاسب، …"
            maxLength={80}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="r-role">
            الدور الأساسي
          </label>
          <select
            id="r-role"
            className="select"
            value={baseRole}
            onChange={(e) => setBaseRole(e.target.value as MembershipRole)}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {MEMBERSHIP_ROLE_MAP[r].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{MEMBERSHIP_ROLE_MAP[baseRole].description}</span>
        </div>
        <div className="field span-2">
          <label className="label" htmlFor="r-desc">
            وصف
          </label>
          <input
            id="r-desc"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
          />
        </div>
      </div>

      {/* ---------------- الصلاحياتُ التفصيليّة (نمط زوهو) ---------------- */}
      <div className="perm-fine mt-4">
        <div className="perm-intro">
          <span className="user-hr-ic">
            <Icon name="layers" size={17} />
          </span>
          <span className="user-hr-txt">
            <strong>الصلاحيات — تحكّمٌ بكلّ فعلٍ على حدة</strong>
            <span>
              حدّد بالضبط ما يفعله هذا الملفُّ في كلِّ وحدة. عند اختيارِ فعلٍ يلزمه فعلٌ آخرُ
              يُحدَّد تلقائيًّا، وما لا تحدّده يُمنع.
            </span>
          </span>
        </div>

        {ACCESS_MODULE_DEFS.filter((mod) =>
            ENFORCED_GROUPS.some(
              (gk) => PERMISSION_GROUPS.find((x) => x.key === gk)?.module === mod.key,
            ),
          ).map((mod) => (
            <div className="perm-module" key={mod.key}>
              <div className="perm-module-head">{mod.label}</div>
              {ENFORCED_GROUPS.filter(
                (gk) => PERMISSION_GROUPS.find((x) => x.key === gk)?.module === mod.key,
              ).map((gk) => {
                const g = PERMISSION_GROUPS.find((x) => x.key === gk);
                if (!g) return null;
                return (
                  <div className="perm-grp" key={g.key}>
                    <label className="perm-grp-head">
                      <input
                        type="checkbox"
                        checked={g.actions.every((a) => actions.has(a.key))}
                        onChange={(e) => setGroup(g.key, e.target.checked)}
                      />
                      {g.label}
                    </label>
                    <div className="perm-acts">
                      {g.actions.map((a) => {
                        const on = actions.has(a.key);
                        const reqLabels = (a.requires ?? [])
                          .map((r) => PERMISSION_ACTION_MAP[r]?.label)
                          .filter(Boolean);
                        return (
                          <label key={a.key} className={`perm-act${on ? ' is-on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={(e) => toggleAction(a.key, e.target.checked)}
                            />
                            <span className="perm-act-body">
                              <span className="perm-act-label">{a.label}</span>
                              {reqLabels.length > 0 && (
                                <span className="tiny mute">يتطلّب: {reqLabels.join('، ')}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        <p className="tiny mute" style={{ margin: '2px' }}>
          عند اختيار فعلٍ يلزمه فعلٌ آخر، يُحدَّد اللازمُ تلقائيًّا. وترك أفعالِ جزءٍ كلِّها بلا
          تحديدٍ يمنعه تمامًا. ومستوياتُ الوحداتِ تُشتقّ من اختيارك تلقائيًّا.
        </p>
      </div>

      <p className="tiny mute mt-3">
        الملفُّ الشخصيّ والإشعاراتُ وبياناتُ الموظّف عن نفسه متاحةٌ للجميع مهما ضاق الملفّ. والمالكُ
        بلا قيود.
      </p>
    </Modal>
  );
}
